<template>
  <div class="relative" v-bind="rootAttrs">
    <s-input-select
      ref="inputSelect"
      v-bind="filteredProps"
      :options="displayedOptions"
      :trackBy="trackBy || 'value'"
      :searchable="true"
      dynamic
      @update:model-value="handleValueUpdate"
      @update:search-string="handleSearchInput"
      @open="handleOpen"
      @scroll-to-bottom="handleScrollToBottom"
    >
      <template v-for="(_, slot) in $slots" :key="slot" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope || {}"></slot>
      </template>
    </s-input-select>

    <s-icon v-if="showLoader" class="loader spinner" type="loader" color="theme-secondary" spinner />
  </div>
</template>

<script>
  import SInputSelect from '@veasel/inputs/input-select';
  import SIcon from '@veasel/base/icon';
  import {debounce} from '@veasel/core/tools';

  export default {
    name: 's-input-select-dynamic',
    description:
      'A select component that loads options dynamically from a backend API with pagination and search support',
    inheritAttrs: false,

    components: {
      SInputSelect,
      SIcon,
    },

    props: {
      fetchOptions: {
        description:
          'The function that will be called to fetch options from backend. Should accept an object with { limit, offset, search_string, ids, names }. Must return a Promise that resolves to: { options: Array, num_entries: Number }',
        type: Function,
        required: true,
      },

      modelValue: {
        description: 'Initial (and selected) value for the component',
        type: [String, Number, Array, Object],
        default: null,
      },

      limit: {
        description: 'Number of options to fetch in each request',
        type: Number,
        default: 30,
      },

      minSearchLength: {
        description: 'Minimum number of characters required to trigger a search',
        type: Number,
        default: 0,
      },

      searchDebounce: {
        description: 'Delay in milliseconds between user input and search execution',
        type: Number,
        default: 200,
      },

      infiniteScroll: {
        description: 'Automatically load the next page when scrolling to the bottom',
        type: Boolean,
        default: true,
      },

      loadingDelay: {
        description: 'Add a preloader delay to avoid flickering for fast responses',
        type: Number,
        default: 10,
      },

      trackBy: {
        description: 'The uniq key used to identify the options.',
        type: String,
      },

      sort: {
        description: 'A flag to sort the options alphabetically.',
        type: Boolean,
        default: true,
      },
      multiple: {
        description: 'A flag to allow multiple selection.',
        type: Boolean,
      },

      tagging: {
        description: 'A flag to allow selecting values not listed in the options list.',
        type: Boolean,
      },
      min: {
        description: 'The minimum number of item to select.',
        type: Number,
      },
      max: {
        description: 'The maximum number of item to select.',
        type: Number,
      },
      hideSelected: {
        description: 'A flag to remove the selected options from the option list.',
        type: Boolean,
        default: false,
      },
      maxHeight: {
        description: 'The maximum height the input can grow to.',
        type: Number,
      },
      overflow: {
        description: 'Indicates whether the input dropdown should escape from scrollable overflow containers',
        type: String,
        values: ['auto', 'enabled', 'disabled'],
        validator: (v) => ['auto', 'enabled', 'disabled'].includes(v),
        default: 'auto',
      },
      keepOpenOnSelect: {
        description: 'A flag to keep the select opened after selection.',
        type: Boolean,
        default: false,
      },

      size: {
        description: 'Size of the input',
        type: String,
      },
      stretchContent: {
        description: 'A flag to make the dropdown width stretch to the longest option.',
        type: Boolean,
        default: false,
      },
    },

    emits: ['update:modelValue', 'search-complete', 'loading-state-change', 'error', 'update:options'],

    data() {
      return {
        localOptions: [],
        cachedOptions: {},
        isLoading: false,
        showLoader: false,
        totalCount: 0,
        offset: 0,
        hasMoreOptions: false,
        searchString: '',
        observer: null,
        scrollContainer: null,
        loadingTimeout: null,
        debouncedSearchUpdate: () => {},
      };
    },

    computed: {
      filteredProps() {
        const props = {...this.inputAttrs, ...this.$props};
        delete props.fetchOptions;
        delete props.limit;
        delete props.minSearchLength;
        delete props.searchDebounce;
        delete props.infiniteScroll;
        delete props.loadingDelay;
        delete props.trackBy;
        return props;
      },
      inputAttrs() {
        const attrs = {...this.$attrs};
        delete attrs['data-testid'];
        delete attrs.class;
        delete attrs.style;
        return attrs;
      },
      rootAttrs() {
        const attrs = {};
        if (this.$attrs['data-testid']) {
          attrs['data-testid'] = this.$attrs['data-testid'];
        }
        if (this.$attrs.class) {
          attrs.class = this.$attrs.class;
        }
        if (this.$attrs.style) {
          attrs.style = this.$attrs.style;
        }
        return {
          ...attrs,
        };
      },
      displayedOptions() {
        const selectedCachedOptions = this.getValuesToCheck(this.modelValue)
          .map((value) => this.cachedOptions[this.getCacheKey(value)])
          .filter((option) => option !== undefined);
        const optionsByKey = {};

        this.localOptions.concat(selectedCachedOptions).forEach((option) => {
          const cacheKey = this.getCacheKey(option);

          if (cacheKey !== null) {
            optionsByKey[cacheKey] = option;
          }
        });

        return Object.values(optionsByKey);
      },
    },

    watch: {
      modelValue: {
        immediate: false, // Don't run on initial value since we handle that in loadOptions
        handler(newValue, oldValue) {
          if (newValue && newValue !== oldValue && !this.isLoading) {
            this.$nextTick(() => {
              this.reconcileValue(newValue);
            });
          }
        },
      },
    },

    created() {
      this.debouncedSearchUpdate = debounce(this.searchUpdate, this.searchDebounce);
      this.loadOptions();
    },

    mounted() {
      if (this.infiniteScroll) {
        this.$nextTick(() => {
          this.setupInfiniteScroll();
        });
      }
    },

    beforeUnmount() {
      this.disconnectObserver();
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
        this.loadingTimeout = null;
      }

      if (this.scrollContainer) {
        this.scrollContainer.removeEventListener('scroll', this.handleScroll);
      }
    },

    methods: {
      getTrackBy() {
        return this.trackBy || 'value';
      },

      getOptionValue(option) {
        if (option && typeof option === 'object') {
          return option[this.getTrackBy()];
        }

        return option;
      },

      getValuesToCheck(value) {
        if (Array.isArray(value)) {
          return value;
        }

        return value === undefined || value === null || value === '' ? [] : [value];
      },

      getCacheKey(option) {
        const value = this.getOptionValue(option);

        return value === undefined || value === null ? null : String(value);
      },

      cacheOptions(options) {
        if (!Array.isArray(options) || options.length === 0) {
          return;
        }

        const nextCachedOptions = {...this.cachedOptions};

        options.forEach((option) => {
          const cacheKey = this.getCacheKey(option);

          if (cacheKey !== null) {
            nextCachedOptions[cacheKey] = option;
          }
        });

        this.cachedOptions = nextCachedOptions;
      },

      hasCachedValue(value) {
        const cacheKey = this.getCacheKey(value);

        return cacheKey !== null && this.cachedOptions[cacheKey] !== undefined;
      },

      handleValueUpdate(value) {
        this.$emit('update:modelValue', value === null ? (this.multiple ? [] : undefined) : value);
      },

      handleSearchInput(searchString) {
        if (searchString !== this.searchString) {
          this.searchString = searchString;
          this.showLoader = true;
          this.$emit('loading-state-change', true);
          this.debouncedSearchUpdate(searchString);
        }
      },

      setupInfiniteScroll() {
        if (!this.infiniteScroll) return;

        this.$nextTick(() => {
          const selectEl = this.$refs.inputSelect?.$el;
          if (!selectEl) return;

          const contentWrapper = selectEl.querySelector('.multiselect__content-wrapper');
          if (!contentWrapper) return;

          this.scrollContainer = contentWrapper;

          if (window.IntersectionObserver && this.$refs.loadMoreIndicator) {
            this.observer = new IntersectionObserver(this.handleIntersection, {
              root: contentWrapper,
              threshold: 0.1,
            });

            this.observer.observe(this.$refs.loadMoreIndicator);
          } else {
            contentWrapper.addEventListener('scroll', this.handleScroll);
          }
        });
      },

      handleIntersection(entries) {
        if (entries[0].isIntersecting && !this.isLoading && this.hasMoreOptions) {
          this.loadMoreOptions();
        }
      },

      handleScroll() {
        if (this.isLoading || !this.hasMoreOptions || !this.scrollContainer) return;

        const scrollPosition = this.scrollContainer.scrollTop + this.scrollContainer.clientHeight;
        const scrollHeight = this.scrollContainer.scrollHeight;

        if (scrollPosition >= scrollHeight * 0.8) {
          this.loadMoreOptions();
        }
      },

      handleOpen() {
        if (this.infiniteScroll) {
          this.$nextTick(() => {
            this.setupInfiniteScroll();
          });
        }
      },

      handleScrollToBottom() {
        if (this.infiniteScroll && this.hasMoreOptions && !this.isLoading) {
          this.loadMoreOptions();
        }
      },

      disconnectObserver() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      },

      searchUpdate(query) {
        this.searchString = query || '';

        if (this.minSearchLength > 0 && query.length < this.minSearchLength) {
          return;
        }

        this.offset = 0;
        this.localOptions = [];
        this.loadOptions();
      },

      loadMoreOptions() {
        if (this.isLoading || !this.hasMoreOptions) return;

        this.offset += this.limit;
        this.loadOptions(true);
      },

      // Check and reconcile initial value after first page load
      async checkAndReconcileInitialValue() {
        if (!this.modelValue || this.isLoading) return;

        // Handle array values (multiple selection)
        const valuesToCheck = this.getValuesToCheck(this.modelValue);
        const allValuesFound = valuesToCheck.every((val) => this.hasCachedValue(val));

        // If not all values are found, run fetchOptions with the missing values directly
        if (!allValuesFound) {
          const missingValues = valuesToCheck.filter((val) => !this.hasCachedValue(val));

          if (missingValues.length > 0) {
            await this.performInitialReconciliation(missingValues);
          }
        }
      },

      // Perform initial reconciliation by requesting the missing values directly
      async performInitialReconciliation(values) {
        try {
          this.isLoading = true;
          this.$emit('loading-state-change', true);

          const params = this.buildReconciliationParams(values);

          const response = await this.fetchOptions(params);
          this.cacheOptions(response.options);

          // If we found options, merge them with existing first page options
          if (response.options && response.options.length > 0) {
            const newOptions = response.options.filter(
              (newOption) =>
                !this.localOptions.some(
                  (existingOption) => this.getCacheKey(existingOption) === this.getCacheKey(newOption)
                )
            );

            if (newOptions.length > 0) {
              // Add new options to the existing first page options
              this.localOptions = [...this.localOptions, ...newOptions];

              // Update total count if needed
              this.totalCount = Math.max(this.totalCount, response.num_entries || 0);
              this.hasMoreOptions = this.localOptions.length < this.totalCount;

              // Emit updated options
              this.$emit('update:options', this.localOptions);
            }
          }
        } catch (error) {
          // Silently handle reconciliation errors
          console.warn('Initial reconciliation failed:', error);
        } finally {
          this.isLoading = false;
          this.$emit('loading-state-change', false);
        }
      },

      // Check if value exists in current options and search for it if not found
      reconcileValue(value) {
        // Skip if no value or already loading
        if (!value || this.isLoading) return;

        // Handle array values (multiple selection)
        const valuesToCheck = this.getValuesToCheck(value);
        const allValuesFound = valuesToCheck.every((val) => this.hasCachedValue(val));

        // If not all values are found, search for missing ones
        if (!allValuesFound) {
          const missingValues = valuesToCheck.filter((val) => !this.hasCachedValue(val));

          if (missingValues.length > 0) {
            this.performReconciliationSearch(missingValues);
          }
        }
      },

      // Perform search to find missing values without affecting current search state
      async performReconciliationSearch(values) {
        try {
          // Store current state
          const originalSearchString = this.searchString;
          const originalOffset = this.offset;

          const params = this.buildReconciliationParams(values);

          const response = await this.fetchOptions(params);
          this.cacheOptions(response.options);

          // If we found options, merge them with existing ones (avoiding duplicates)
          if (response.options && response.options.length > 0) {
            const newOptions = response.options.filter(
              (newOption) =>
                !this.localOptions.some(
                  (existingOption) => this.getCacheKey(existingOption) === this.getCacheKey(newOption)
                )
            );

            if (newOptions.length > 0) {
              // Add new options to the existing ones
              this.localOptions = [...this.localOptions, ...newOptions];

              // Update total count (it might have changed)
              this.totalCount = Math.max(this.totalCount, response.num_entries || 0);
              this.hasMoreOptions = this.localOptions.length < this.totalCount;

              // Emit updated options
              this.$emit('update:options', this.localOptions);
            }
          }

          // Restore original state (don't affect current search/pagination)
          this.searchString = originalSearchString;
          this.offset = originalOffset;
        } catch (error) {
          // Silently handle reconciliation errors - they shouldn't disrupt normal operation
          console.warn('Reconciliation search failed:', error);
        }
      },

      buildReconciliationParams(values) {
        return {
          limit: Math.max(this.limit, values.length),
          offset: 0,
          ids: values,
        };
      },

      async loadOptions(append = false) {
        // Prevent multiple simultaneous loads
        if (this.isLoading) return;

        // Always set loading state immediately to prevent multiple calls
        this.isLoading = true;
        this.$emit('loading-state-change', true);

        // Handle visual loading delay
        if (this.loadingDelay > 0) {
          this.loadingTimeout = setTimeout(() => {
            this.showLoader = true;
          }, this.loadingDelay);
        } else {
          this.showLoader = true;
        }

        try {
          const params = {
            limit: this.limit,
            offset: this.offset,
            search_string: this.searchString,
          };

          const response = await this.fetchOptions(params);
          this.cacheOptions(response.options);

          this.totalCount = response.num_entries || 0;

          if (append) {
            this.localOptions = [...this.localOptions, ...response.options];
          } else {
            this.localOptions = response.options || [];
          }
          this.hasMoreOptions = this.localOptions.length < this.totalCount;

          this.$emit('update:options', this.localOptions);
          this.$emit('search-complete', {
            options: this.localOptions,
            totalCount: this.totalCount,
            hasMore: this.hasMoreOptions,
          });

          // After initial load, check if we need to reconcile existing modelValue
          if (!append && this.modelValue) {
            this.$nextTick(() => {
              this.checkAndReconcileInitialValue();
            });
          }
        } catch (error) {
          this.$emit('error', error);
        } finally {
          // Clear any pending loading timeout
          if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
            this.loadingTimeout = null;
          }

          this.isLoading = false;
          this.showLoader = false;
          this.$emit('loading-state-change', false);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@veasel/core';

  .loading-indicator,
  .load-more-indicator {
    padding: 8px;
    text-align: center;
    color: var(--secondary);
    font-size: 0.8rem;
  }

  .loading-indicator {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .loader {
    position: absolute;
    top: calc(50% - 0.5rem);
    right: 1.5rem;
  }
</style>
